/**
 * VH-102: Recipe-level video — tabbed YouTube link or file upload.
 *
 * Sources are mutually exclusive. Pasting a YouTube URL or uploading a file
 * clears the other on the server. The player below switches between an
 * <iframe> (YouTube) and an HTML5 <video> (uploaded file).
 */

import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  VideoCameraIcon,
  LinkIcon,
  ArrowUpTrayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import {
  useSetRecipeVideoYoutube,
  useUploadRecipeVideoFile,
  useDeleteRecipeVideo,
} from '../../hooks/useRecipes'

// Picker hint — accepts these extensions in the file dialog. The actual
// validation below allows any video/* MIME plus a filename-extension fallback,
// because browsers report less common formats with non-standard MIME (e.g.
// "video/x-matroska" for .mkv) and codec-laden MIME like
// "video/mp4; codecs=…".
const ACCEPTED_VIDEO_HINT = 'video/*,.mp4,.mov,.webm,.mkv,.avi,.m4v,.3gp,.ogv'
const KNOWN_VIDEO_EXTS = ['mp4', 'mov', 'webm', 'mkv', 'avi', 'm4v', '3gp', 'ogv']
const MAX_VIDEO_BYTES = 1024 * 1024 * 1024 // 1 GB — matches backend cap

function isAcceptedVideo(file) {
  if (!file) return false
  const type = (file.type || '').toLowerCase()
  if (type.startsWith('video/')) return true
  // Fallback: filename extension when the browser didn't set a MIME.
  const name = (file.name || '').toLowerCase()
  const dot = name.lastIndexOf('.')
  if (dot < 0) return false
  return KNOWN_VIDEO_EXTS.includes(name.slice(dot + 1))
}

// Lightweight client-side YouTube URL pre-check. The server canonicalizes
// authoritatively; this prevents an obviously bad value from ever leaving
// the browser.
const YOUTUBE_HOST_RE =
  /^(https?:\/\/)?(www\.|m\.)?(youtube\.com|youtu\.be)\//i

export default function RecipeVideoSection({ recipe }) {
  const { t } = useTranslation()

  const setYoutube = useSetRecipeVideoYoutube(recipe.id)
  const uploadFile = useUploadRecipeVideoFile(recipe.id)
  const deleteVideo = useDeleteRecipeVideo(recipe.id)
  const busy =
    setYoutube.isPending ||
    uploadFile.isPending ||
    deleteVideo.isPending

  const hasYoutube = !!recipe.video_youtube_url
  const hasUpload = !!recipe.video_file_url
  const hasAny = hasYoutube || hasUpload

  // Default tab: keep showing whatever the recipe has; otherwise YouTube.
  const [tab, setTab] = useState(hasUpload ? 'upload' : 'youtube')
  const [urlInput, setUrlInput] = useState(recipe.video_youtube_url || '')
  const [error, setError] = useState(null)

  const fileRef = useRef(null)
  const [dragOver, setDragOver] = useState(false)

  const switchTab = (target) => {
    if (target === tab) return
    if (
      (target === 'upload' && hasYoutube) ||
      (target === 'youtube' && hasUpload)
    ) {
      const ok = window.confirm(t('recipes.video.confirmReplace'))
      if (!ok) return
    }
    setError(null)
    setTab(target)
  }

  const submitYoutube = (event) => {
    event?.preventDefault?.()
    const value = urlInput.trim()
    if (!value || !YOUTUBE_HOST_RE.test(value)) {
      setError(t('recipes.video.youtubeInvalid'))
      return
    }
    setError(null)
    setYoutube.mutate(value, {
      onError: (e) =>
        setError(
          e?.response?.status === 401
            ? t('recipes.video.errorSessionExpired')
            : e?.response?.data?.detail || t('recipes.video.youtubeInvalid')
        ),
    })
  }

  const validateFile = (file) => {
    if (!file) return t('recipes.video.errorUpload')
    if (!isAcceptedVideo(file)) return t('recipes.video.wrongFormat')
    if (file.size > MAX_VIDEO_BYTES) return t('recipes.video.errorUpload')
    return null
  }

  const submitFile = (file) => {
    const err = validateFile(file)
    if (err) {
      setError(err)
      return
    }
    if (hasAny) {
      const ok = window.confirm(t('recipes.video.confirmReplace'))
      if (!ok) return
    }
    setError(null)
    const fd = new FormData()
    fd.append('file', file)
    uploadFile.mutate(fd, {
      // A 401 here means the silent token-refresh already failed and the
      // session is dead — prompt re-login rather than a generic upload error.
      onError: (err) =>
        setError(
          err?.response?.status === 401
            ? t('recipes.video.errorSessionExpired')
            : t('recipes.video.errorUpload')
        ),
    })
  }

  const handleFileDrop = (event) => {
    event.preventDefault()
    setDragOver(false)
    submitFile(event.dataTransfer?.files?.[0])
  }

  const handleRemove = () => {
    setError(null)
    deleteVideo.mutate()
  }

  return (
    <div className="space-y-3" data-testid="recipe-video-section">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-gray-900">
          {t('recipes.video.sectionTitle')}
        </div>
        {hasAny && (
          <button
            type="button"
            disabled={busy}
            onClick={handleRemove}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
            data-testid="recipe-video-remove"
          >
            <TrashIcon className="h-3.5 w-3.5" />
            {t('recipes.video.remove')}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div
        role="tablist"
        className="inline-flex rounded-md border border-gray-300 overflow-hidden"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'youtube'}
          onClick={() => switchTab('youtube')}
          className={`px-3 py-1.5 text-xs flex items-center gap-1 ${
            tab === 'youtube'
              ? 'bg-primary-50 text-primary-700'
              : 'bg-surface-container text-gray-700 hover:bg-gray-50'
          }`}
          data-testid="recipe-video-tab-youtube"
        >
          <LinkIcon className="h-3.5 w-3.5" />
          {t('recipes.video.tabYoutube')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'upload'}
          onClick={() => switchTab('upload')}
          className={`px-3 py-1.5 text-xs flex items-center gap-1 ${
            tab === 'upload'
              ? 'bg-primary-50 text-primary-700'
              : 'bg-surface-container text-gray-700 hover:bg-gray-50'
          }`}
          data-testid="recipe-video-tab-upload"
        >
          <ArrowUpTrayIcon className="h-3.5 w-3.5" />
          {t('recipes.video.tabUpload')}
        </button>
      </div>

      {tab === 'youtube' ? (
        <form
          onSubmit={submitYoutube}
          className="flex gap-2"
          data-testid="recipe-video-youtube-form"
        >
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder={t('recipes.video.youtubePlaceholder')}
            className="flex-1 px-3 py-1.5 text-sm"
            data-testid="recipe-video-youtube-input"
          />
          <button
            type="submit"
            disabled={busy}
            className="btn-primary text-sm"
            data-testid="recipe-video-youtube-submit"
          >
            {t('recipes.video.youtubeSave')}
          </button>
        </form>
      ) : (
        <div
          onClick={() => !busy && fileRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleFileDrop}
          className={`border-2 border-dashed rounded-lg p-5 text-center cursor-pointer ${
            dragOver
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 hover:border-gray-400 bg-gray-50'
          } ${busy ? 'opacity-60 cursor-wait' : ''}`}
          data-testid="recipe-video-upload-dropzone"
        >
          <VideoCameraIcon className="mx-auto h-9 w-9 text-gray-400" />
          <p className="mt-2 text-sm text-gray-700">
            {busy
              ? t('recipes.video.uploading')
              : t('recipes.video.uploadDropZone')}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {t('recipes.video.uploadSupported')}
          </p>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED_VIDEO_HINT}
            className="hidden"
            onChange={(e) => submitFile(e.target.files?.[0])}
            data-testid="recipe-video-file-input"
          />
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600" data-testid="recipe-video-error">
          {error}
        </p>
      )}

      {hasAny && (
        <RecipeVideoPlayer
          youtubeUrl={recipe.video_youtube_url}
          fileUrl={recipe.video_file_url}
        />
      )}
    </div>
  )
}

function RecipeVideoPlayer({ youtubeUrl, fileUrl }) {
  if (youtubeUrl) {
    return (
      <div
        className="relative w-full"
        style={{ paddingTop: '56.25%' }}
        data-testid="recipe-video-player-youtube"
      >
        <iframe
          src={youtubeUrl}
          title="recipe video"
          className="absolute inset-0 w-full h-full rounded-lg border border-gray-200"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    )
  }
  if (fileUrl) {
    return (
      <video
        controls
        preload="metadata"
        // iOS Safari / iPad Firefox (all iOS browsers use WKWebView) refuse
        // to play HTML5 video inline without `playsinline`. Without it the
        // tap-to-play handler tries to enter fullscreen and silently fails
        // in some embedded contexts. The webkit-prefixed variant covers
        // older iOS versions.
        playsInline
        webkit-playsinline=""
        src={fileUrl}
        className="w-full max-h-96 rounded-lg border border-gray-200"
        data-testid="recipe-video-player-file"
      />
    )
  }
  return null
}
